import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '@/lib/api';

const AutoPopupsCtx = createContext({
  enabled: true,
  ready: false,
  setEnabled: () => {},
  refresh: async () => {},
});

/**
 * Global auto-popup master switch (admin-controlled, all users).
 * Defaults to enabled while loading so existing behaviour is preserved if the
 * flag endpoint is slow/unavailable. Only disables when the server explicitly
 * returns auto_popups_enabled === false.
 */
export function AutoPopupsProvider({ children }) {
  const [enabled, setEnabledState] = useState(true);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/ux-flags');
      setEnabledState(data?.auto_popups_enabled !== false);
    } catch {
      // Keep current / default-on on network errors — don't brick the app.
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setEnabled = useCallback((v) => {
    setEnabledState(!!v);
  }, []);

  return (
    <AutoPopupsCtx.Provider value={{ enabled, ready, setEnabled, refresh }}>
      {children}
    </AutoPopupsCtx.Provider>
  );
}

export function useAutoPopups() {
  return useContext(AutoPopupsCtx);
}

export default AutoPopupsProvider;
